import { useQuery, useQueryClient } from "@tanstack/react-query";
import { uniq } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useFormTemplates } from "src/components/Providers/FormTemplateProvider";
import { normalizeSearchQuery } from "src/components/graphql/utils";
import {
  AttendanceTypes,
  FILTER_DELIMITER,
  FormTabsTypes,
  SearchAndFilterTypes,
  getAdmissionTab,
} from "src/constants";
import { useListFormsKey } from "src/generated/hooks/form-service/queries";
import {
  getForms,
  listFormIdsAdvancedV2,
  listFormIdsV2,
  listForms,
  listFormsAdvanced,
} from "src/generated/hooks/form-service/requests";
import {
  useOrderByParams,
  usePaginationParams,
} from "src/hooks/useCommonSearchParams";
import { useFormTemplatePermissions } from "src/hooks/useFormTemplatePermissions";
import { useGlossary } from "src/hooks/useGlossary";
import { Selection } from "src/hooks/useMultiselectState";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { FormListImportRow } from "src/scenes/orgAdmin/forms/components/ImportMenuInput";
import { GET_METADATA_BY_FORM_TEMPLATE } from "src/scenes/orgAdmin/forms/graphql/queries";
import {
  FormIdListResult,
  FormList,
  FormListRow,
  SchoolFormKeyRecordFactory,
} from "src/scenes/orgAdmin/forms/types";
import { getFormIdFromRow } from "src/services/dataTransfer";
import {
  mapFormServiceError,
  throwIfFormServiceError,
} from "src/services/error";
import * as FormTemplate from "src/services/formTemplate";
import * as Question from "src/services/formTemplate/question";
import { stringsAsEnums } from "src/types/enum";
import * as GQL from "src/types/graphql";
import * as RemoteData from "src/types/remoteData";
import { clearFilters, verifyIfFiltersAreApplied } from "../formFilters/utils";
import {
  ColumnId,
  deprecatedColumnKeyToCurrentColumnKey,
} from "../formTables/constants";
import {
  SearchParamDefaultOverrides,
  useFormsListTab,
} from "./useFormsListTab";

// These columns' data are NOT fetched in the basic listForms endpoint of form service
const NON_BASIC_LIST_FORMS_COLUMNS: ColumnId[] = [
  ColumnId.AttendanceStatus,
  ColumnId.AttendingSchool,
  ColumnId.AttendingSchoolId,
  ColumnId.AttendingSchoolReferenceId,
  ColumnId.GuardianFirstName,
  ColumnId.GuardianMiddleName,
  ColumnId.GuardianLastName,
  ColumnId.GuardianId,
  ColumnId.GuardianAddress,
  ColumnId.GuardianEmail,
  ColumnId.GuardianPhone,
  ColumnId.GuardianLanguage,
  // Question columns are also non-basic, but their ids are dynamically formatted as "question-<question-id>"
];
function hasNonBasicListFormsColumns(visibleColumns: string[]) {
  const hasQuestionColumn = visibleColumns.some((columnId) =>
    columnId.startsWith(ColumnId.Question)
  );
  const hasOtherNonBasicColumn = NON_BASIC_LIST_FORMS_COLUMNS.some((columnId) =>
    visibleColumns.includes(columnId)
  );
  return hasQuestionColumn || hasOtherNonBasicColumn;
}

export type UseFormsListProps = {
  enrollmentPeriodId?: uuid | undefined;
  formTemplateId?: uuid | undefined;
  searchParamDefaultOverrides?: SearchParamDefaultOverrides;
};
export function useFormsList({
  enrollmentPeriodId,
  formTemplateId,
  searchParamDefaultOverrides,
}: UseFormsListProps = {}) {
  const { glossary } = useGlossary();
  const { setOrderBy } = useOrderByParams();
  const { pagination, setPagination } = usePaginationParams();
  const { selectedNavFormTemplate } = useFormTemplates();

  /**
   * Search
   */

  const [searchParams, setSearchParams] = useSearchParams();
  enrollmentPeriodId =
    enrollmentPeriodId ??
    searchParams.get(SearchAndFilterTypes.EnrollmentPeriod) ??
    "";

  formTemplateId = useMemo(
    () => formTemplateId ?? selectedNavFormTemplate?.id,
    [formTemplateId, selectedNavFormTemplate]
  );

  const [selection, setSelection] = useState(
    Selection.create((app: FormListRow) => SchoolFormKeyRecordFactory(app.id))
  );
  const setSearchParamsAndClearSelection: typeof setSearchParams = useCallback(
    (nextInit, navigateOptions) => {
      setSearchParams(nextInit, { replace: true, ...navigateOptions });
      setSelection((s) => s.clear());
    },
    [setSearchParams, setSelection]
  );

  const onClear = useCallback(() => {
    searchParams.delete(SearchAndFilterTypes.Search);
    searchParams.delete(SearchAndFilterTypes.Offset);
    setSearchParamsAndClearSelection(searchParams);
  }, [searchParams, setSearchParamsAndClearSelection]);

  const onSearch = useCallback(
    (searchTerm: string) => {
      const normalized = normalizeSearchQuery(searchTerm);
      searchParams.set(SearchAndFilterTypes.Search, normalized);
      searchParams.delete(SearchAndFilterTypes.Offset);
      setSearchParamsAndClearSelection(searchParams);
    },
    [searchParams, setSearchParamsAndClearSelection]
  );

  const isSearchApplied = searchParams.has(SearchAndFilterTypes.Search);

  // Handle backward compatibility deprecated column key in sort key
  useEffect(() => {
    const sortKey = searchParams.get(SearchAndFilterTypes.SortKey);
    if (sortKey) {
      const currentColumnKey = deprecatedColumnKeyToCurrentColumnKey[sortKey];
      if (currentColumnKey) {
        // Deprecated column key detected, update URL to new format
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set(SearchAndFilterTypes.SortKey, currentColumnKey);
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  const visibleColumns = useMemo(() => {
    const visibleColumnsParam = searchParams.get(
      SearchAndFilterTypes.VisibleColumns
    );
    if (!visibleColumnsParam) return [];
    return visibleColumnsParam.split(FILTER_DELIMITER);
  }, [searchParams]);

  // Check if additional data needs to be fetched
  const additionalDataNeeded = useMemo(
    () => hasNonBasicListFormsColumns(visibleColumns),
    [visibleColumns]
  );

  // Waitlist position is expensive to compute; form-service only includes it when
  // tab=waitlists or includeWaitlistPosition=true. Opt in when the column is visible.
  const includeWaitlistPosition = useMemo(
    () => visibleColumns.includes(ColumnId.WaitlistPosition),
    [visibleColumns]
  );

  // Clear selection whenever enrollment period, search, or filters change.
  useEffect(() => {
    setSelection((s) => s.clear());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentPeriodId, formTemplateId]);

  /** Form Import */
  const [formListImportData, setFormListImportData] = useState<
    FormListImportRow[]
  >([]);

  const handleOnFormListImported = (data: FormListImportRow[]) => {
    setFormListImportData(data);

    const formListImportsTab = getAdmissionTab(FormTabsTypes.FormListImports);
    if (!formListImportsTab) {
      throw new Error("Something went wrong!");
    }

    tabs.onChange({ ...formListImportsTab, isVisible: true });
  };

  /**
   * Tabs
   */
  const tabs = useFormsListTab({
    formTemplateId,
    searchParams,
    setSearchParamsAndClearSelection,
    setFormListImportData,
    searchParamDefaultOverrides,
  });

  const isFilterApplied = verifyIfFiltersAreApplied(searchParams);

  const handleClearFilters = useCallback(() => {
    searchParams.delete(SearchAndFilterTypes.Offset);
    clearFilters(searchParams);
    setSearchParamsAndClearSelection(searchParams);
  }, [searchParams, setSearchParamsAndClearSelection]);

  /**
   * Forms List
   */
  const selectAll = useCallback(
    (count: number) => {
      setSelection((s) => s.selectAll(count));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const clearAll = useCallback(() => {
    searchParams.delete(SearchAndFilterTypes.Search);
    searchParams.delete(SearchAndFilterTypes.Offset);
    clearFilters(searchParams);
    setSearchParamsAndClearSelection(searchParams);
  }, [searchParams, setSearchParamsAndClearSelection]);

  const formTemplateData = useFormTemplateMetadata({
    form_template_id: formTemplateId ?? "",
  });

  const previousFormTemplateId =
    tabs.current.type === FormTabsTypes.DropoffForms
      ? tabs.current.formTemplateId
      : "";
  const previousFormTemplateData = useFormTemplateMetadata({
    form_template_id: previousFormTemplateId,
  });

  const queryParams = useMemo(
    () => ({
      ...(previousFormTemplateId
        ? {
            formTemplateId: previousFormTemplateId,
            assignedFormTemplateId: formTemplateId ?? "",
          }
        : { formTemplateId: formTemplateId ?? "" }),
      ...Object.fromEntries(searchParams.entries()),
      includeWaitlistPosition,
    }),
    [
      formTemplateId,
      previousFormTemplateId,
      searchParams,
      includeWaitlistPosition,
    ]
  );
  const bodyParams = useMemo(() => {
    if (!formListImportData.length) return undefined;
    return {
      formSchoolSelection: formListImportData.map((item) => ({
        formId: getFormIdFromRow(item) ?? "",
        schoolId: item["School ID"] || null,
      })),
    };
  }, [formListImportData]);

  const listFormsResponse = useQuery({
    queryKey: [useListFormsKey, queryParams, bodyParams, additionalDataNeeded],
    queryFn: async () => {
      const basicResponse = await (bodyParams ? listFormsAdvanced : listForms)({
        query: queryParams,
        body: bodyParams,
      })
        .then(throwIfFormServiceError)
        .then(({ data }) => {
          return {
            ...data,
            rows: data.rows.map((row) => ({
              ...row,
              student: row.person,
              guardian: null,
              attendingSchool: null,
              answers: null,
            })),
          };
        });

      // Fetch additional data when needed
      const formIds = uniq(basicResponse.rows.map((row) => row.id.formId));
      if (!additionalDataNeeded || !formIds.length) {
        return { data: basicResponse };
      }

      const formTemplate = formTemplateData.toNullable();
      const questionIds = Question.getQuestionIdsInOrder(
        formTemplate ? FormTemplate.fromGQL(formTemplate).sections : undefined
      );

      const expandedData = await getForms({
        body: { data: { formIds, questionIds } },
      })
        .then(throwIfFormServiceError)
        .then(({ data }) => data);

      // Merge basic + additional data
      const expandedRows = basicResponse.rows.map((row) => {
        const expandedRowData = expandedData.find(
          (form) => form.id === row.id.formId
        );
        return {
          ...row,
          guardian: expandedRowData?.applicant.guardians[0] ?? null, // TODO: handle multiple guardians
          attendingSchool: expandedRowData?.applicant?.attendingSchool ?? null,
          answers: expandedRowData?.answers ?? null,
        };
      });

      return {
        data: {
          totalCount: basicResponse.totalCount,
          rows: expandedRows,
        },
      };
    },
    enabled: !!queryParams.formTemplateId && formTemplateData.hasData(),
    staleTime: 2000, // Allow 2 seconds for React to settle.
    refetchOnWindowFocus: false,
  });
  const formData = useMemo(() => {
    return RemoteData.fromRestResult(listFormsResponse)
      .mapError(mapFormServiceError("Error fetching forms."))
      .map(({ data }) => stringsAsEnums<FormList>(data));
  }, [listFormsResponse]);
  const refetch = useCallback(() => {
    const refetchPromise = listFormsResponse.refetch();
    // Refetch is usually called to indicate the results may have changed, so we
    // clear the selection as it may contain forms no longer in the list.
    setSelection((s) => s.clear());
    return refetchPromise;
  }, [listFormsResponse]);

  const fetchAllIds = useCallback(
    (): Promise<FormIdListResult> =>
      (bodyParams ? listFormIdsAdvancedV2 : listFormIdsV2)({
        query: queryParams,
        body: bodyParams,
      })
        .then(throwIfFormServiceError)
        .then(({ data }) => data),
    [queryParams, bodyParams]
  );

  const handleAttendanceFilterChange = useCallback(
    (value: string) => {
      if (value === searchParams.get(SearchAndFilterTypes.Attendance)) return;
      switch (value) {
        case AttendanceTypes.Applying: {
          searchParams.set(
            SearchAndFilterTypes.Attendance,
            AttendanceTypes.Applying
          );
          searchParams.delete(SearchAndFilterTypes.School);
          searchParams.delete(SearchAndFilterTypes.AttendingSchool);
          break;
        }
        case AttendanceTypes.Attending: {
          searchParams.set(
            SearchAndFilterTypes.Attendance,
            AttendanceTypes.Attending
          );
          searchParams.delete(SearchAndFilterTypes.School);
          searchParams.delete(SearchAndFilterTypes.AttendingSchool);
          break;
        }
        default: {
          searchParams.delete(SearchAndFilterTypes.Attendance);
          break;
        }
      }
      setSearchParamsAndClearSelection(searchParams);
    },
    [searchParams, setSearchParamsAndClearSelection]
  );

  return {
    tabs,
    search: {
      onSearch,
      value: searchParams.get(SearchAndFilterTypes.Search) ?? "",
      onClear,
      placeholder: glossary`Search by form ID, student name or ID, parent name or ID`,
      params: searchParams,
      isApplied: isSearchApplied,
    },
    remoteData: {
      formData,
      formTemplateData,
      previousFormTemplateData,
    },
    formsList: {
      clearAll,
      selectAll,
      refetch,
      fetchAllIds,
      sort: setOrderBy,
      imported: handleOnFormListImported,
      setAttendance: handleAttendanceFilterChange,
    },
    setSearchParamsAndClearSelection,
    setSelection,
    selection,
    pagination,
    setPagination,
    filter: {
      clear: handleClearFilters,
      isApplied: isFilterApplied,
    },
    enrollmentPeriodId,
  };
}

export function useInvalidateFormList() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [useListFormsKey] });
  }, [queryClient]);
}

function useFormTemplateMetadata(
  variables: GQL.GetMetadataByFormTemplateVariables
) {
  const { pruneFormTemplate } = useFormTemplatePermissions();
  const { remoteData } = useRemoteDataQuery<
    GQL.GetMetadataByFormTemplate,
    GQL.GetMetadataByFormTemplateVariables
  >(GET_METADATA_BY_FORM_TEMPLATE, {
    variables,
    skip: !variables.form_template_id,
  });
  return useMemo(
    () =>
      remoteData
        .castError<Error>()
        .andThen<GQL.GetMetadataByFormTemplate_form_template_by_pk>((data) =>
          data.form_template_by_pk
            ? RemoteData.success(data.form_template_by_pk)
            : RemoteData.failure(new Error("Invalid form"))
        )
        .map(pruneFormTemplate),
    [remoteData, pruneFormTemplate]
  );
}
